import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { toast } from "sonner";
import BreathOrb from "../breathwork/BreathOrb";
import { ChevronLeft, X, ChevronRight, Leaf, HeartPulse, Moon, Flower2, CheckCircle2 } from "lucide-react";

const STAGES = [
  { key: "early", titleKey: "panic_stage_early_title", subKey: "panic_stage_early_sub", techniqueKey: "panic_tech_2_4", techniqueId: "2_4_breathing", introTitleKey: "panic_intro_early_title", introBodyKey: "panic_intro_early_body", bodyHintKey: "panic_body_hint_early", color: "#7A9EBF", icon: Leaf, rounds: 6, phases: [{ key: "inhale", seconds: 2 }, { key: "exhale", seconds: 4 }] },
  { key: "acute", titleKey: "panic_stage_acute_title", subKey: "panic_stage_acute_sub", techniqueKey: "panic_tech_pursed", techniqueId: "pursed_lip", introTitleKey: "panic_intro_acute_title", introBodyKey: "panic_intro_acute_body", bodyHintKey: "panic_body_hint_acute", color: "#D48A8A", icon: HeartPulse, rounds: 5, phases: [{ key: "inhale", seconds: 3 }, { key: "exhale", seconds: 6 }] },
  { key: "subside", titleKey: "panic_stage_subside_title", subKey: "panic_stage_subside_sub", techniqueKey: "panic_tech_478", techniqueId: "4_7_8", introTitleKey: "panic_intro_subside_title", introBodyKey: "panic_intro_subside_body", bodyHintKey: "panic_body_hint_subside", color: "#B196C4", icon: Moon, rounds: 4, phases: [{ key: "inhale", seconds: 4 }, { key: "hold_in", seconds: 7 }, { key: "exhale", seconds: 8 }] },
  { key: "prevent", titleKey: "panic_stage_prevent_title", subKey: "panic_stage_prevent_sub", techniqueKey: "panic_tech_diaphragm", techniqueId: "diaphragmatic", introTitleKey: "panic_intro_prevent_title", introBodyKey: "panic_intro_prevent_body", bodyHintKey: "panic_body_hint_prevent", color: "#8FB3A0", icon: Flower2, rounds: 6, phases: [{ key: "inhale", seconds: 4 }, { key: "exhale", seconds: 6 }] },
];

function Scale({ value, onChange, prefix, accent }) {
  return (
    <div className="grid grid-cols-10 gap-1.5">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
        const on = value === n;
        return (
          <button key={n} onClick={() => onChange(n)} data-testid={`${prefix}-${n}`}
            className="flex h-10 items-center justify-center rounded-lg border text-sm font-semibold transition-colors"
            style={on ? { backgroundColor: accent, borderColor: accent, color: "#fff" } : { borderColor: "hsl(var(--border))", color: "hsl(var(--foreground))" }}>{n}</button>
        );
      })}
    </div>
  );
}

export default function PanicFlow() {
  const { t } = useI18n();
  const navigate = useNavigate();
  usePageMeta(t("panic_attack_btn"));
  const [step, setStep] = useState(0);
  const [stageKey, setStageKey] = useState(null);
  const [before, setBefore] = useState(null);
  const [now, setNow] = useState(null);
  const [run, setRun] = useState({ completed: false, elapsed_seconds: 0 });
  const stage = STAGES.find((s) => s.key === stageKey) || null;

  const goHome = () => navigate("/app/breathwork");
  const onBack = () => { if (step === 0) navigate("/app/breathwork"); else if (step === 4) goHome(); else setStep(step - 1); };

  const submit = () => {
    if (!stage || !before || !now) return;
    api.post("/panic-sessions", { stage_key: stage.key, technique_id: stage.techniqueId, intensity_before: before, intensity_after: now, duration_seconds: run.elapsed_seconds, completed: run.completed }).catch(() => toast.error(t("something_went_wrong")));
    setStep(4);
  };

  if (step === 2 && stage) {
    return (
      <div className="mx-auto max-w-xl" data-testid="panic-flow-player">
        <BreathOrb phases={stage.phases} rounds={stage.rounds} techniqueLabel={t(stage.techniqueKey)}
          onComplete={(r) => { setRun(r); setStep(3); }} onCancel={() => setStep(1)} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl space-y-4" data-testid="panic-flow-screen">
      <div className="flex items-center gap-3">
        <button onClick={onBack} data-testid="panic-back" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent">{step === 0 ? <X className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}</button>
        <h1 className="font-display text-xl text-foreground">{t("panic_attack_btn")}</h1>
      </div>

      {step === 0 ? (
        <div className="space-y-3">
          <div>
            <h2 className="font-display text-2xl text-foreground">{t("panic_picker_title")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t("panic_picker_sub")}</p>
          </div>
          {STAGES.map((s) => (
            <button key={s.key} onClick={() => { setStageKey(s.key); setStep(1); }} data-testid={`panic-card-${s.key}`}
              className="flex w-full items-center gap-3 rounded-2xl border bg-card p-3 text-left transition-all hover:-translate-y-0.5 hover:shadow-md" style={{ borderColor: s.color }}>
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: s.color + "22" }}><s.icon className="h-5 w-5" style={{ color: s.color }} /></span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-display text-sm text-foreground">{t(s.titleKey)}</span>
                <span className="block truncate text-xs text-muted-foreground">{t(s.subKey)}</span>
                <span className="block truncate text-[11px] font-medium" style={{ color: s.color }}>{t("panic_technique_label")} · {t(s.techniqueKey)}</span>
              </span>
              <ChevronRight className="h-5 w-5" style={{ color: s.color }} />
            </button>
          ))}
        </div>
      ) : null}

      {step === 1 && stage ? (
        <div className="space-y-4 text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full" style={{ backgroundColor: stage.color + "22" }}><stage.icon className="h-8 w-8" style={{ color: stage.color }} /></span>
          <h2 className="font-display text-2xl text-foreground">{t(stage.introTitleKey)}</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{t(stage.introBodyKey)}</p>
          <div className="rounded-2xl border border-border bg-secondary/40 p-4 text-left">
            <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: stage.color }}>{t("explanation_how_it_works")}</p>
            <p className="mt-1 text-sm text-foreground">{t(stage.bodyHintKey)}</p>
          </div>
          <button onClick={() => setStep(2)} data-testid="panic-explanation-ready" className="w-full rounded-full py-3 font-semibold text-white" style={{ backgroundColor: stage.color }}>{t("explanation_ready_btn")}</button>
        </div>
      ) : null}

      {step === 3 && stage ? (
        <div className="space-y-5">
          <div>
            <h2 className="font-display text-2xl text-foreground">{t("panic_intensity_recap_title")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t("panic_intensity_scale_hint")}</p>
          </div>
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-foreground">{t("copd_intensity_before_short")}</p>
            <Scale value={before} onChange={setBefore} prefix="panic-before" accent={stage.color} />
          </div>
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-foreground">{t("copd_intensity_after_short")}</p>
            <Scale value={now} onChange={setNow} prefix="panic-after" accent={stage.color} />
          </div>
          <button onClick={submit} disabled={!before || !now} data-testid="panic-intensity-continue" className="w-full rounded-full py-3 font-semibold text-white disabled:opacity-40" style={{ backgroundColor: stage.color }}>{t("panic_intensity_continue")}</button>
        </div>
      ) : null}

      {step === 4 && stage ? (
        <div className="flex flex-col items-center gap-4 py-10 text-center">
          <span className="flex h-20 w-20 items-center justify-center rounded-full" style={{ backgroundColor: stage.color + "22" }}><CheckCircle2 className="h-10 w-10" style={{ color: stage.color }} /></span>
          <h2 className="font-display text-2xl text-foreground">{t("session_success_title")}</h2>
          <p className="max-w-sm text-sm text-muted-foreground">{t("session_success_body")}</p>
          <div className="flex w-full max-w-xs flex-col gap-2">
            <button onClick={goHome} data-testid="panic-success-done" className="w-full rounded-full py-3 font-semibold text-white" style={{ backgroundColor: stage.color }}>{t("session_done_btn")}</button>
            <button onClick={() => navigate("/app/panic/analytics")} data-testid="panic-goto-analytics" className="w-full rounded-full border border-border py-2.5 text-sm font-medium text-foreground hover:bg-accent">{t("panic_analytics_title")}</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
